// src/services/tenantAdminCreditService.js

import axiosInstance from '../config/axios.config';

class TenantAdminCreditService {
    /**
     * Get all users' credit wallets with pagination and filters
     * @param {Object} params - Query parameters for pagination and filtering
     * @returns {Promise} API response with wallets list
     */
    async getUsersWallets(params = {}) {
        try {
            const response = await axiosInstance.get('/admin/credits/users/wallets', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching users wallets:', error);
            throw error;
        }
    }

    /**
     * Get companies for filter dropdown (reuse existing endpoint)
     * @param {Object} params - Optional search/pagination params
     * @returns {Promise} API response with companies list
     */
    async getCompaniesForFilter(params = {}) {
        try {
            const response = await axiosInstance.get('/admin/companies', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching companies:', error);
            throw error;
        }
    }

    /**
     * Get users for filter dropdown (reuse existing endpoint)
     * @param {Object} params - Optional search/filter params
     * @returns {Promise} API response with users list
     */
    async getUsersForFilter(params = {}) {
        try {
            const response = await axiosInstance.get('/users', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching users:', error);
            throw error;
        }
    }

    /**
     * Get credit transfer metrics (This Month, Quarter, Half Year, This Year)
     * @returns {Promise} API response with metrics data
     */
    async getCreditTransferMetrics() {
        try {
            const response = await axiosInstance.get('/admin/credits/metrics');
            return response.data;
        } catch (error) {
            console.error('Error fetching credit transfer metrics:', error);
            throw error;
        }
    }

    /**
     * Transfer credits to user (Tenant Admin → User)
     * @param {number} userId - User ID
     * @param {number} credits - Amount of credits to transfer
     * @param {string} reason - Reason for transfer (optional)
     * @param {string} note - Additional note (optional)
     * @returns {Promise} API response with transfer result
     */
    async transferCredits(userId, credits, reason = null, note = null) {
        try {
            const response = await axiosInstance.post('/admin/credits/transfer', {
                user_id: userId,
                credits: credits,
                reason: reason,
                note: note
            });
            return response.data;
        } catch (error) {
            console.error('Error transferring credits:', error);
            throw error;
        }
    }

    /**
     * Reclaim credits from user (User → Tenant Admin)
     * @param {number} userId - User ID
     * @param {number} credits - Amount of credits to reclaim
     * @param {string} reason - Reason for reclaiming (REQUIRED)
     * @param {string} note - Additional note (optional)
     * @returns {Promise} API response with reclaim result
     */
    async reclaimCredits(userId, credits, reason, note = null) {
        try {
            const response = await axiosInstance.post('/admin/credits/reclaim', {
                user_id: userId,
                credits: credits,
                reason: reason,
                note: note
            });
            return response.data;
        } catch (error) {
            console.error('Error reclaiming credits:', error);
            throw error;
        }
    }

    /**
     * Get user's complete transaction history
     * @param {number} userId - User ID
     * @param {Object} params - Query parameters (page, limit, type, date_from, date_to)
     * @returns {Promise} API response with paginated transactions
     */
    async getUserTransactions(userId, params = {}) {
        try {
            const response = await axiosInstance.get(`/admin/credits/users/${userId}/transactions`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching user transactions:', error);
            throw error;
        }
    }

    /**
     * Update user details
     * @param {number} userId - User ID
     * @param {Object} data - User details (name, email, first_name, last_name, mobile_number)
     * @returns {Promise} API response with update result
     */
    async updateUserDetails(userId, data) {
        try {
            const response = await axiosInstance.put(`/admin/credits/users/${userId}/details`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating user details:', error);
            throw error;
        }
    }

    /**
     * Format currency amount
     * @param {number} amount - Amount
     * @returns {string} Formatted currency string
     */
    formatCurrency(amount) {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount);
    }

    /**
     * Format date for display
     * @param {string} dateString - ISO date string
     * @returns {string} Formatted date string
     */
    formatDate(dateString) {
        if (!dateString) return '-';
        return new Intl.DateTimeFormat('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(new Date(dateString));
    }

    /**
     * Format credits with proper thousands separator
     * @param {number} credits - Credits amount
     * @returns {string} Formatted credits string
     */
    formatCredits(credits) {
        return new Intl.NumberFormat('en-IN').format(credits);
    }
}

export default new TenantAdminCreditService();
